/**
 * FRONT-2235: the shared Created/Updated precedence rule for anything that
 * carries both timestamps (a project note, a saved document, an uploaded
 * file) — one place so every surface that shows the pair agrees on when
 * "Updated" is worth showing at all.
 *
 * "Updated" is genuinely new information only when it would read
 * differently from "Created" once both are formatted for a person to read.
 * A document whose `updatedAt` defaults to its `createdAt` (never edited)
 * would otherwise show "Created 19 Aug 2026, 14:32 · Updated 19 Aug 2026,
 * 14:32" — a pointless echo. So Updated shows only when BOTH hold: it is at
 * least `UPDATED_MIN_DELTA_MS` after Created (clears clock-skew and
 * same-transaction default-to-created noise), AND its formatted string
 * actually differs from Created's (clears the case where a real few-second
 * gap still lands in the same displayed minute).
 */

import { formatDateTime } from './local-datetime';

/**
 * The minimum gap between createdAt and updatedAt before "Updated" is
 * considered a real edit rather than clock noise around the row's own
 * creation (e.g. a default column write happening a few ms after the
 * insert). Not a measured perf constant — a deliberately generous product
 * threshold chosen so no genuinely untouched row can accidentally clear it.
 */
export const UPDATED_MIN_DELTA_MS = 5_000;

export interface ItemDates {
  /** Already formatted via `formatDateTime` — undefined when there is
   *  nothing valid to show. */
  created?: string;
  /** Present only when the item was genuinely edited — see the rule above. */
  updated?: string;
}

function toValidDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Resolve the Created/Updated pair for one item. Pure — no rendering, no
 * DOM — so it's testable independent of `formatDateTime`'s own
 * locale-dependent output (a test computes its own expected strings with
 * `formatDateTime` too, rather than hardcoding a locale-specific literal).
 */
export function resolveItemDates(
  createdAt: string | number | Date | null | undefined,
  updatedAt: string | number | Date | null | undefined,
): ItemDates {
  const createdDate = toValidDate(createdAt);
  const updatedDate = toValidDate(updatedAt);

  if (!createdDate && !updatedDate) return {};

  if (!createdDate) {
    // Valid updatedAt with no createdAt to compare against — nothing to
    // derive "was it edited" from, so show just the one fact we have.
    return { updated: formatDateTime(updatedDate) };
  }

  const createdStr = formatDateTime(createdDate);

  if (!updatedDate) return { created: createdStr };

  // A stored updatedAt earlier than createdAt is not a real edit (bad data
  // or a default-value quirk) — never surface it as one.
  if (updatedDate.getTime() < createdDate.getTime()) return { created: createdStr };

  if (updatedDate.getTime() - createdDate.getTime() < UPDATED_MIN_DELTA_MS) {
    return { created: createdStr };
  }

  const updatedStr = formatDateTime(updatedDate);
  if (updatedStr === createdStr) return { created: createdStr };

  return { created: createdStr, updated: updatedStr };
}

/** The raw (unformatted) createdAt/updatedAt pair `selectLiveItemDates`
 *  chooses between — either a live detail-query result or a list/feed
 *  snapshot. Deliberately has NO `timestamp` field: the zen modal's byline
 *  bug (FRONT-2235 review F2) was reading a snapshot's feed-ordering
 *  `timestamp` as if it were the real edit time, so the type this function
 *  accepts cannot express that field at all — a caller that wants to pass
 *  one has to rename it first, which is the point. */
export interface LiveItemDatesSource {
  createdAt?: string;
  updatedAt?: string;
}

/**
 * FRONT-2235 review F2: the zen modal byline's live-over-snapshot precedence
 * for Created/Updated, extracted so it's testable without rendering. `live`
 * is the detail query result (`useDocumentDetail`/`useFileDetail`) — present
 * once it resolves, `undefined` before that and for a chat/briefing asset
 * that has no detail route. `snapshot` is the list/feed row the modal opened
 * with (`FeedItem`/`FileAssetItem`) — for an asset rebuilt from a bare
 * session id (`sessionToModalAsset`'s stub) it carries neither field, so
 * both resolve to `undefined` and the byline shows no date line until the
 * live fetch lands. Live wins field-by-field (not object-by-object) so a
 * document whose live createdAt has arrived but whose updatedAt query is
 * still pending doesn't regress a value the snapshot already had.
 */
export function selectLiveItemDates(
  live: LiveItemDatesSource | undefined,
  snapshot: LiveItemDatesSource | undefined,
): LiveItemDatesSource {
  return {
    createdAt: live?.createdAt ?? snapshot?.createdAt,
    updatedAt: live?.updatedAt ?? snapshot?.updatedAt,
  };
}
